// Role Management Blueprint v2.3 · Phase 2 — Role Form rebuild (registry-driven).
//
// This component is the FE consumer of the Business Capability Registry
// (v2.3 §4.3). Every admin-facing string is resolved from the registry:
//   * GET /admin/access/taxonomy       — Module → Workflow → Function → Capability tree
//   * GET /admin/access/capability-codes-map — silent id→perm-codes lookup for
//                                              save-time payload construction
//   * GET /admin/role/:code            — existing role (edit mode)
//
// Save contract is UNCHANGED (v2.3 §12):
//   POST /admin/role                 body { code, name, description, permissions[] }
//   PATCH /admin/role/{code}         body { name?, description?, permissions?, is_active? }
// Permission codes are derived silently from ticked Capabilities. Codes
// are NEVER rendered anywhere in the UI. PDE / permission-code /
// permission-master / advanced-details concepts are all gone.
//
// Rendering rules (Blueprint §5, §11):
//   * Every business string comes from the registry (business_name /
//     business_description / capability_type / risk_level).
//   * Capabilities are grouped by their module → workflow → function.
//     Module-scope capabilities float above workflows; workflow-scope
//     capabilities float above functions.
//   * Automatic / identity-derived (class-C) capabilities render as
//     non-selectable info chips ("Auto-granted based on role").
//   * Data Scope pill is read-only (Blueprint §8).
//   * Deprecated capabilities render with a muted marker; retired
//     capabilities are omitted by the backend so they never appear.
//   * Dependencies (required / recommended / conflict) render in plain
//     business language.
//   * ANY codes present on the existing role that don't map to any
//     Capability are preserved through save as opaque "extra codes" —
//     defensive back-compat so orphan grants aren't silently dropped.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
    ArrowLeft, Warning, Check, Info, Sparkle, XCircle, Lightning,
    CaretDown, CaretRight as CaretRightIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";

// Visual treatments driven purely by registry attributes — no hardcoded
// business labels or permission-code strings.
const RISK_STYLES = {
    Low: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Medium: "bg-slate-100 text-slate-700 border-slate-200",
    High: "bg-orange-100 text-orange-700 border-orange-200",
    Critical: "bg-rose-100 text-rose-700 border-rose-200",
};

const TYPE_STYLES = {
    View: "bg-slate-50 text-slate-700 border-slate-200",
    Maintain: "bg-sky-50 text-sky-700 border-sky-200",
    Transact: "bg-blue-50 text-blue-700 border-blue-200",
    Approve: "bg-violet-50 text-violet-700 border-violet-200",
    Process: "bg-indigo-50 text-indigo-700 border-indigo-200",
    Administration: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
    Reporting: "bg-teal-50 text-teal-700 border-teal-200",
    Governance: "bg-amber-50 text-amber-800 border-amber-200",
    Onboarding: "bg-cyan-50 text-cyan-700 border-cyan-200",
    Automation: "bg-pink-50 text-pink-700 border-pink-200",
};

const DEP_TYPE_LABEL = {
    required: "Requires",
    recommended: "Suggested",
    conflict: "Conflicts with",
};

const DEP_TYPE_STYLES = {
    required: "text-indigo-700",
    recommended: "text-amber-700",
    conflict: "text-rose-700",
};

export default function RoleForm({ mode }) {
    const navigate = useNavigate();
    const { code: codeParam } = useParams();
    const isEdit = mode === "edit";

    // ── Registry data (source of truth) ──────────────────────────
    const [taxonomy, setTaxonomy] = useState(null);
    const [codesByCapability, setCodesByCapability] = useState({});
    const [classCCapabilities, setClassCCapabilities] = useState(new Set());
    const [scopeLabels, setScopeLabels] = useState({});

    // ── Form state ────────────────────────────────────────────────
    const [form, setForm] = useState({ code: "", name: "", description: "" });
    const [checkedCaps, setCheckedCaps] = useState(new Set());
    const [extraCodes, setExtraCodes] = useState([]);
    const [isSystem, setIsSystem] = useState(false);

    // ── UI state ──────────────────────────────────────────────────
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [collapsedModules, setCollapsedModules] = useState({});
    const [query, setQuery] = useState("");

    // ── Flatten the taxonomy tree to a working capability list ───
    const flatCapabilities = useMemo(() => {
        if (!taxonomy) return [];
        const out = [];
        for (const m of taxonomy.modules || []) {
            for (const c of m.capabilities || []) {
                out.push({ ...c, _module: m, _workflow: null, _function: null });
            }
            for (const w of m.workflows || []) {
                for (const c of w.capabilities || []) {
                    out.push({ ...c, _module: m, _workflow: w, _function: null });
                }
                for (const f of w.functions || []) {
                    for (const c of f.capabilities || []) {
                        out.push({ ...c, _module: m, _workflow: w, _function: f });
                    }
                }
            }
        }
        return out;
    }, [taxonomy]);

    const capabilityById = useMemo(() => {
        const out = {};
        for (const c of flatCapabilities) {
            out[c.capability_identifier] = c;
        }
        return out;
    }, [flatCapabilities]);

    // ── Initial load ──────────────────────────────────────────────
    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [taxRes, codesRes] = await Promise.all([
                api.get("/admin/access/taxonomy"),
                api.get("/admin/access/capability-codes-map"),
            ]);
            setTaxonomy(taxRes.data);
            setCodesByCapability(taxRes.data && codesRes.data.codes_by_capability || {});
            setClassCCapabilities(new Set(codesRes.data.class_c_capabilities || []));
            const scopes = {};
            for (const s of taxRes.data.data_scopes || []) scopes[s.identifier] = s.label;
            setScopeLabels(scopes);

            if (isEdit && codeParam) {
                const roleRes = await api.get(`/admin/role/${codeParam}`);
                const role = roleRes.data;
                setForm({
                    code: role.code,
                    name: role.name,
                    description: role.description || "",
                });
                setIsSystem(!!role.is_system);
                // Reconstruct capability checkboxes from the role's
                // saved permissions[]. A capability is "checked" iff
                // every one of its save codes is present in the role.
                const rolePerms = new Set(role.permissions || []);
                const initialChecked = new Set();
                const usedCodes = new Set();
                for (const [cid, codes] of Object.entries(codesRes.data.codes_by_capability || {})) {
                    if (!codes.length) continue; // class-C: no save contribution
                    const allPresent = codes.every((c) => rolePerms.has(c));
                    if (allPresent) {
                        initialChecked.add(cid);
                        codes.forEach((c) => usedCodes.add(c));
                    }
                }
                // Any code not explainable by a ticked capability is
                // preserved as an "extra code" so we never silently
                // strip a legacy grant on Save.
                const extras = [...rolePerms].filter((c) => !usedCodes.has(c)).sort();
                setExtraCodes(extras);
                setCheckedCaps(initialChecked);
            } else {
                setForm({ code: "", name: "", description: "" });
                setCheckedCaps(new Set());
                setExtraCodes([]);
                setIsSystem(false);
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [codeParam, isEdit]);

    useEffect(() => { load(); }, [load]);

    // ── Derived: the codes we'll save ────────────────────────────
    const savePermissions = useMemo(() => {
        const codes = new Set(extraCodes);
        for (const cid of checkedCaps) {
            const list = codesByCapability[cid] || [];
            list.forEach((c) => codes.add(c));
        }
        return [...codes].sort();
    }, [checkedCaps, extraCodes, codesByCapability]);

    // ── Search filter (matches business fields only) ─────────────
    const matchesQuery = useCallback((cap) => {
        if (!query.trim()) return true;
        const q = query.trim().toLowerCase();
        return (
            (cap.business_name || "").toLowerCase().includes(q) ||
            (cap.business_description || "").toLowerCase().includes(q) ||
            (cap._module?.label || "").toLowerCase().includes(q) ||
            (cap._workflow?.label || "").toLowerCase().includes(q) ||
            (cap._function?.label || "").toLowerCase().includes(q) ||
            (cap.capability_type || "").toLowerCase().includes(q) ||
            (cap.risk_level || "").toLowerCase().includes(q)
        );
    }, [query]);

    // ── Mutators ──────────────────────────────────────────────────
    const toggleCapability = (cid) => {
        // Class-C capabilities are informational; not user-toggleable.
        if (classCCapabilities.has(cid)) return;
        setCheckedCaps((prev) => {
            const next = new Set(prev);
            if (next.has(cid)) next.delete(cid); else next.add(cid);
            return next;
        });
    };

    const toggleAllInGroup = (capIds, selectAll) => {
        setCheckedCaps((prev) => {
            const next = new Set(prev);
            for (const cid of capIds) {
                if (classCCapabilities.has(cid)) continue;
                if (selectAll) next.add(cid); else next.delete(cid);
            }
            return next;
        });
    };

    // ── Save flow ─────────────────────────────────────────────────
    const submit = async () => {
        if (!form.code.trim() || !form.name.trim()) {
            toast.error("Code and Name are required.");
            return;
        }
        if (isEdit && form.code === "super_admin") {
            toast.error("This role is locked and cannot be edited.");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                name: form.name.trim(),
                description: form.description.trim(),
                permissions: savePermissions,
            };
            if (isEdit) {
                await api.patch(`/admin/role/${form.code}`, payload);
                toast.success(`Role updated with ${checkedCaps.size} capabilities.`);
            } else {
                await api.post("/admin/role", { ...payload, code: form.code.trim().toLowerCase() });
                toast.success("Role created.");
            }
            navigate("/admin/roles");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    // ── Counts for save summary ──────────────────────────────────
    const counts = useMemo(() => {
        const total = flatCapabilities.filter(
            (c) => c.deprecation?.status !== "retired" && !classCCapabilities.has(c.capability_identifier),
        ).length;
        const chosen = checkedCaps.size;
        const byModule = {};
        for (const cid of checkedCaps) {
            const cap = capabilityById[cid];
            if (!cap) continue;
            const modLabel = cap._module?.label || "—";
            byModule[modLabel] = (byModule[modLabel] || 0) + 1;
        }
        return { total, chosen, byModule };
    }, [checkedCaps, flatCapabilities, capabilityById, classCCapabilities]);

    if (loading) {
        return <div className="p-6 text-sm text-slate-500" data-testid="role-form-loading">Loading…</div>;
    }

    const editLockedForSystem = isEdit && form.code === "super_admin";

    return (
        <div className="space-y-5 pb-20" data-testid="role-form-page">
            <PageHeader
                title={isEdit ? `Edit role: ${form.name}` : "New role"}
                subtitle={isEdit ? `Code: ${form.code}` : "Create a new application role"}
                breadcrumbs={[
                    { label: "Administration" },
                    { label: "Roles", to: "/admin/roles" },
                    { label: isEdit ? "Edit" : "New" },
                ]}
                actions={
                    <Link to="/admin/roles">
                        <Button variant="outline" data-testid="back-to-roles-btn">
                            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Roles
                        </Button>
                    </Link>
                }
            />

            {/* System-role banners */}
            {editLockedForSystem && (
                <div className="bg-amber-50 border border-amber-300 text-amber-800 rounded-lg px-4 py-3 text-sm flex items-start gap-2" data-testid="super-admin-immutable-banner">
                    <Warning className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <div>
                        <div className="font-medium">This role is locked.</div>
                        <div className="text-xs">Primary administrative access is preserved as-is. Its capability set cannot be edited.</div>
                    </div>
                </div>
            )}
            {isEdit && isSystem && !editLockedForSystem && (
                <div className="bg-indigo-50 border border-indigo-300 text-indigo-800 rounded-lg px-4 py-3 text-sm" data-testid="system-role-banner">
                    <span className="font-medium">System role.</span> Its code cannot change. You may edit the name, description, and capability grants.
                </div>
            )}
            {extraCodes.length > 0 && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm flex items-start gap-2" data-testid="role-form-legacy-notice">
                    <Info className="w-5 h-5 flex-shrink-0 mt-0.5 text-slate-500" />
                    <div>
                        <div className="font-medium text-slate-900">Legacy access preserved</div>
                        <div className="text-xs text-slate-600 mt-0.5">
                            This role carries {extraCodes.length} legacy access grant{extraCodes.length === 1 ? "" : "s"} that predate the current registry. They will be kept intact on save.
                        </div>
                    </div>
                </div>
            )}

            {/* 3-pane body */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5" data-testid="role-form-three-pane">

                {/* LEFT — Identity + Save Summary */}
                <div className="lg:col-span-3 space-y-4" data-testid="role-form-pane-left">
                    <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
                        <h3 className="font-semibold text-slate-900 text-sm">Identity</h3>
                        <div className="space-y-1.5">
                            <Label htmlFor="role-code">
                                Code <span className="text-rose-500">*</span>
                            </Label>
                            <Input
                                id="role-code"
                                value={form.code}
                                onChange={(e) => setForm((p) => ({ ...p, code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") }))}
                                placeholder="developer"
                                disabled={isEdit}
                                data-testid="role-code-input"
                            />
                            <p className="text-[10px] text-slate-500">
                                Lowercase, [a-z0-9_]. 2-32 chars.
                            </p>
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="role-name">
                                Display name <span className="text-rose-500">*</span>
                            </Label>
                            <Input
                                id="role-name"
                                value={form.name}
                                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                                placeholder="Sales Ops"
                                disabled={editLockedForSystem}
                                data-testid="role-name-input"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="role-desc">Description</Label>
                            <Textarea
                                id="role-desc"
                                value={form.description}
                                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                                placeholder="Short description shown to admins"
                                rows={3}
                                disabled={editLockedForSystem}
                                data-testid="role-description-input"
                            />
                        </div>
                    </div>

                    <SaveSummaryCard counts={counts} />
                </div>

                {/* CENTER — Capability tree */}
                <div className="lg:col-span-5 bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="role-form-pane-center">
                    <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50 flex items-center gap-3">
                        <div className="text-xs uppercase tracking-wide text-slate-600 shrink-0">
                            Capabilities ({counts.total})
                        </div>
                        <div className="flex-1">
                            <Input
                                placeholder="Search…"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                className="h-8 text-xs"
                                data-testid="role-form-search-input"
                            />
                        </div>
                        <div className="font-mono text-xs text-slate-500 shrink-0">
                            {counts.chosen} selected
                        </div>
                    </div>
                    <div className="max-h-[calc(100vh-340px)] overflow-y-auto" data-testid="role-form-catalog">
                        {(taxonomy?.modules || []).map((m) => {
                            const modCapIds = [
                                ...m.capabilities.map((c) => c.capability_identifier),
                                ...m.workflows.flatMap((w) => [
                                    ...w.capabilities.map((c) => c.capability_identifier),
                                    ...w.functions.flatMap((f) => f.capabilities.map((c) => c.capability_identifier)),
                                ]),
                            ];
                            const capsInModule = modCapIds
                                .map((cid) => capabilityById[cid])
                                .filter((c) => c && matchesQuery(c));
                            if (!capsInModule.length) return null;
                            return (
                                <ModuleSection
                                    key={m.identifier}
                                    module={m}
                                    isCollapsed={!!collapsedModules[m.identifier]}
                                    onToggleCollapse={() =>
                                        setCollapsedModules((c) => ({ ...c, [m.identifier]: !c[m.identifier] }))
                                    }
                                    capabilityById={capabilityById}
                                    checkedCaps={checkedCaps}
                                    classCCapabilities={classCCapabilities}
                                    onToggleCap={toggleCapability}
                                    onToggleAll={(ids, sel) => toggleAllInGroup(ids, sel)}
                                    scopeLabels={scopeLabels}
                                    matchesQuery={matchesQuery}
                                    disabled={editLockedForSystem}
                                />
                            );
                        })}
                    </div>
                </div>

                {/* RIGHT — Selected capabilities preview */}
                <div className="lg:col-span-4 space-y-4" data-testid="role-form-pane-right">
                    <SelectedPreviewPane
                        checkedCaps={checkedCaps}
                        capabilityById={capabilityById}
                        counts={counts}
                    />
                </div>
            </div>

            {/* Sticky footer */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-6 py-3 flex items-center justify-between z-30" data-testid="role-form-actions">
                <span className="text-xs text-slate-500">
                    {isEdit
                        ? "Capability changes take effect on the next API request (60s cache)."
                        : "Role becomes selectable in the User screen immediately after creation."}
                </span>
                <div className="flex items-center gap-2">
                    <Link to="/admin/roles">
                        <Button variant="outline" data-testid="cancel-role-btn">Cancel</Button>
                    </Link>
                    <Button
                        onClick={submit}
                        disabled={submitting || editLockedForSystem}
                        data-testid="save-role-btn"
                    >
                        <Check className="w-4 h-4 mr-2" />
                        {submitting
                            ? "Saving…"
                            : isEdit
                                ? `Save (${counts.chosen} capabilities)`
                                : `Create role (${counts.chosen} capabilities)`}
                    </Button>
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────
// Sub-components (all business-string only)
// ─────────────────────────────────────────────────────────────────────

function SaveSummaryCard({ counts }) {
    const modules = Object.entries(counts.byModule).sort((a, b) => a[0].localeCompare(b[0]));
    return (
        <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="role-form-save-summary">
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900 text-sm">Summary</h3>
                <span className="font-mono text-sm text-slate-700">{counts.chosen} / {counts.total}</span>
            </div>
            {counts.chosen === 0 ? (
                <p className="text-xs text-slate-500 italic">No capabilities selected yet.</p>
            ) : (
                <div className="space-y-1.5" data-testid="role-form-summary-by-module">
                    {modules.map(([mod, n]) => (
                        <div key={mod} className="flex items-center justify-between text-xs">
                            <span className="text-slate-600">{mod}</span>
                            <span className="font-mono text-slate-700">{n}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function ModuleSection({
    module: mod, isCollapsed, onToggleCollapse,
    capabilityById, checkedCaps, classCCapabilities, onToggleCap, onToggleAll,
    scopeLabels, matchesQuery, disabled,
}) {
    const moduleScopeIds = mod.capabilities.map((c) => c.capability_identifier);
    const workflowMap = mod.workflows.map((w) => ({
        w,
        workflowScopeIds: w.capabilities.map((c) => c.capability_identifier),
        functions: w.functions.map((f) => ({ f, ids: f.capabilities.map((c) => c.capability_identifier) })),
    }));
    const allSelectableIds = [
        ...moduleScopeIds,
        ...workflowMap.flatMap(({ workflowScopeIds, functions }) => [
            ...workflowScopeIds,
            ...functions.flatMap((fn) => fn.ids),
        ]),
    ].filter((cid) => !classCCapabilities.has(cid) && capabilityById[cid]);
    const checkedInModule = allSelectableIds.filter((cid) => checkedCaps.has(cid)).length;
    const allChecked = checkedInModule > 0 && checkedInModule === allSelectableIds.length;

    return (
        <div className="border-b border-slate-100 last:border-0" data-testid={`role-form-module-${mod.identifier}`}>
            <div className="px-4 py-2.5 bg-slate-50/60 flex items-center justify-between">
                <button
                    type="button"
                    onClick={onToggleCollapse}
                    className="flex items-center gap-2 text-[12px] font-semibold text-slate-800 uppercase tracking-wider"
                    data-testid={`role-form-module-toggle-${mod.identifier}`}
                >
                    {isCollapsed ? <CaretRightIcon className="w-3.5 h-3.5" /> : <CaretDown className="w-3.5 h-3.5" />}
                    {mod.label}
                    <span className="font-mono text-slate-400 normal-case font-normal">
                        ({checkedInModule}/{allSelectableIds.length})
                    </span>
                </button>
                {!disabled && allSelectableIds.length > 0 && (
                    <button
                        type="button"
                        onClick={() => onToggleAll(allSelectableIds, !allChecked)}
                        className="text-[11px] font-medium text-indigo-600 hover:text-indigo-700"
                        data-testid={`role-form-module-select-all-${mod.identifier}`}
                    >
                        {allChecked ? "Unselect all" : "Select all"}
                    </button>
                )}
            </div>
            {!isCollapsed && (
                <div className="pl-2">
                    {mod.description && (
                        <div className="px-4 py-1.5 text-[11px] text-slate-500 italic">{mod.description}</div>
                    )}
                    {moduleScopeIds
                        .map((cid) => capabilityById[cid])
                        .filter((c) => c && matchesQuery(c))
                        .map((c) => (
                            <CapabilityRow
                                key={c.capability_identifier}
                                cap={c}
                                checked={checkedCaps.has(c.capability_identifier)}
                                isClassC={classCCapabilities.has(c.capability_identifier)}
                                onToggle={() => onToggleCap(c.capability_identifier)}
                                scopeLabels={scopeLabels}
                                disabled={disabled}
                                indent={0}
                            />
                        ))}
                    {workflowMap.map(({ w, workflowScopeIds, functions }) => {
                        const wfIds = [...workflowScopeIds, ...functions.flatMap((fn) => fn.ids)];
                        const wfCaps = wfIds.map((cid) => capabilityById[cid]).filter((c) => c && matchesQuery(c));
                        if (!wfCaps.length) return null;
                        return (
                            <div key={w.identifier} className="mt-1" data-testid={`role-form-workflow-${w.identifier}`}>
                                <div className="px-4 py-1 text-[11px] font-semibold text-slate-700 uppercase tracking-wider">
                                    {w.label}
                                </div>
                                {workflowScopeIds
                                    .map((cid) => capabilityById[cid])
                                    .filter((c) => c && matchesQuery(c))
                                    .map((c) => (
                                        <CapabilityRow
                                            key={c.capability_identifier}
                                            cap={c}
                                            checked={checkedCaps.has(c.capability_identifier)}
                                            isClassC={classCCapabilities.has(c.capability_identifier)}
                                            onToggle={() => onToggleCap(c.capability_identifier)}
                                            scopeLabels={scopeLabels}
                                            disabled={disabled}
                                            indent={1}
                                        />
                                    ))}
                                {functions.map(({ f, ids }) => {
                                    const fnCaps = ids.map((cid) => capabilityById[cid]).filter((c) => c && matchesQuery(c));
                                    if (!fnCaps.length) return null;
                                    return (
                                        <div key={f.identifier} data-testid={`role-form-function-${f.identifier}`}>
                                            <div className="px-4 py-1 text-[10px] font-medium text-slate-500 uppercase tracking-wider ml-4">
                                                {f.label}
                                            </div>
                                            {fnCaps.map((c) => (
                                                <CapabilityRow
                                                    key={c.capability_identifier}
                                                    cap={c}
                                                    checked={checkedCaps.has(c.capability_identifier)}
                                                    isClassC={classCCapabilities.has(c.capability_identifier)}
                                                    onToggle={() => onToggleCap(c.capability_identifier)}
                                                    scopeLabels={scopeLabels}
                                                    disabled={disabled}
                                                    indent={2}
                                                />
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function CapabilityRow({ cap, checked, isClassC, onToggle, scopeLabels, disabled, indent }) {
    const depStatus = cap.deprecation?.status;
    const isDeprecated = depStatus === "deprecated";
    const rowClasses = [
        "flex items-start gap-3 px-4 py-2.5 border-t border-slate-100",
        isClassC ? "bg-violet-50/30" : (checked ? "bg-indigo-50/40" : "hover:bg-slate-50"),
        disabled || isClassC ? "cursor-default" : "cursor-pointer",
        isDeprecated ? "opacity-70" : "",
    ].join(" ");
    const paddingLeft = { paddingLeft: `${16 + indent * 12}px` };
    const rowInner = (
        <>
            {isClassC ? (
                <span
                    className="mt-1 inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-violet-200 text-violet-800"
                    title="Auto-granted based on role — not manually selectable"
                    data-testid={`role-form-cap-auto-${cap.capability_identifier}`}
                >
                    <Lightning className="w-2.5 h-2.5" />
                </span>
            ) : (
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={onToggle}
                    disabled={disabled}
                    className="mt-1"
                    data-testid={`role-form-cap-cb-${cap.capability_identifier}`}
                    aria-label={cap.business_name}
                />
            )}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm text-slate-900" data-testid={`role-form-cap-name-${cap.capability_identifier}`}>
                        {cap.business_name}
                    </span>
                    {cap.capability_type && (
                        <span className={`px-1.5 py-0.5 rounded-sm text-[9px] font-medium border ${TYPE_STYLES[cap.capability_type] || TYPE_STYLES.View}`}>
                            {cap.capability_type}
                        </span>
                    )}
                    {cap.risk_level && (
                        <span className={`px-1.5 py-0.5 rounded-sm text-[9px] font-medium border ${RISK_STYLES[cap.risk_level] || RISK_STYLES.Medium}`}>
                            {cap.risk_level}
                        </span>
                    )}
                    {cap.default_data_scope && (
                        <span
                            className="px-1.5 py-0.5 rounded-sm text-[9px] font-medium border bg-white text-slate-600 border-slate-200"
                            title={cap.available_data_scopes && cap.available_data_scopes.length > 1
                                ? `Possible scopes: ${cap.available_data_scopes.map((s) => scopeLabels[s] || s).join(", ")}`
                                : undefined}
                            data-testid={`role-form-cap-scope-${cap.capability_identifier}`}
                        >
                            {scopeLabels[cap.default_data_scope] || cap.default_data_scope}
                        </span>
                    )}
                    {cap.flags?.danger && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm text-[9px] font-medium bg-rose-50 text-rose-700 border border-rose-100">
                            <Warning className="w-2.5 h-2.5" /> Sensitive
                        </span>
                    )}
                    {cap.flags?.dual_signoff && (
                        <span className="px-1.5 py-0.5 rounded-sm text-[9px] font-medium bg-amber-50 text-amber-800 border border-amber-100">
                            4-eye
                        </span>
                    )}
                    {cap.flags?.psa_only && (
                        <span className="px-1.5 py-0.5 rounded-sm text-[9px] font-medium bg-fuchsia-50 text-fuchsia-800 border border-fuchsia-100">
                            Primary Admin only
                        </span>
                    )}
                    {isClassC && (
                        <span className="px-1.5 py-0.5 rounded-sm text-[9px] font-medium bg-violet-100 text-violet-800 border border-violet-200">
                            Auto-granted
                        </span>
                    )}
                    {isDeprecated && (
                        <span className="px-1.5 py-0.5 rounded-sm text-[9px] font-medium bg-slate-200 text-slate-700 border border-slate-300">
                            Deprecated
                        </span>
                    )}
                </div>
                {cap.business_description && (
                    <div className="text-[11px] text-slate-500 mt-0.5">{cap.business_description}</div>
                )}
                {isClassC && (
                    <div className="text-[10px] text-violet-600 italic mt-1">
                        This capability is granted automatically based on the operator&apos;s role and cannot be assigned manually.
                    </div>
                )}
                {(cap.dependencies || []).length > 0 && (
                    <div className="mt-1 flex flex-col gap-0.5" data-testid={`role-form-cap-deps-${cap.capability_identifier}`}>
                        {cap.dependencies.map((d, i) => (
                            <div key={i} className={`text-[10px] ${DEP_TYPE_STYLES[d.type] || "text-slate-600"}`}>
                                <span className="font-semibold">{DEP_TYPE_LABEL[d.type] || d.type}:</span>{" "}
                                <span className="italic">{d.reason || d.capability}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </>
    );
    if (isClassC || disabled) {
        return (
            <div className={rowClasses} style={paddingLeft} data-testid={`role-form-cap-row-${cap.capability_identifier}`}>
                {rowInner}
            </div>
        );
    }
    return (
        <label
            className={rowClasses}
            style={paddingLeft}
            data-testid={`role-form-cap-row-${cap.capability_identifier}`}
        >
            {rowInner}
        </label>
    );
}

function SelectedPreviewPane({ checkedCaps, capabilityById, counts }) {
    const selected = [...checkedCaps]
        .map((cid) => capabilityById[cid])
        .filter(Boolean)
        .sort((a, b) => (a._module?.label || "").localeCompare(b._module?.label || "") || (a.business_name || "").localeCompare(b.business_name || ""));

    const grouped = {};
    for (const c of selected) {
        const key = c._module?.label || "—";
        (grouped[key] ||= []).push(c);
    }

    return (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="role-form-selected-pane">
            <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-600 flex items-center justify-between">
                <span>This role can…</span>
                <span className="font-mono text-[10px] text-slate-500">{counts.chosen} capabilities</span>
            </div>
            {counts.chosen === 0 ? (
                <div className="px-4 py-6 text-xs text-slate-400 italic text-center flex items-center justify-center gap-2">
                    <Sparkle className="w-3.5 h-3.5" />
                    Select capabilities to preview the effective role.
                </div>
            ) : (
                <div className="max-h-[calc(100vh-560px)] overflow-y-auto divide-y divide-slate-100">
                    {Object.entries(grouped).map(([mod, caps]) => (
                        <div key={mod}>
                            <div className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50/50">
                                {mod}
                            </div>
                            {caps.map((c) => (
                                <div key={c.capability_identifier} className="px-4 py-1.5" data-testid={`role-form-preview-${c.capability_identifier}`}>
                                    <div className="text-xs text-slate-800">{c.business_name}</div>
                                    {c.flags?.danger && (
                                        <div className="text-[10px] text-rose-700 mt-0.5 flex items-center gap-1">
                                            <XCircle className="w-2.5 h-2.5" /> Sensitive — actions here are audit-logged.
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
